import type { CaptainSave } from '@realseas/shared';
import { getItem, ISLAND_SELL_FRACTION_BY_SIZE } from '@realseas/shared';
import type { Connection } from '../Connection.js';
import { setHTML, esc } from '../dom.js';

function ownedOf(captain: CaptainSave, itemId: string): number {
  return captain.inventory.find(i => i.itemId === itemId)?.quantity ?? 0;
}

const NEVER_SOLD = new Set(['ship_surgeon', 'master_gunner']);

export function renderIslandShopScreen(
  container: HTMLElement,
  conn: Connection,
  captain: CaptainSave,
): void {
  const shop = captain.islandShop;
  if (!shop) {
    setHTML(container, `<p>Loading island...</p>`);
    return;
  }

  const sizeLabel = shop.islandSize === 4 ? 'a major port'
    : shop.islandSize === 3 ? 'a substantial harbor'
    : shop.islandSize === 2 ? 'a small island shop' : 'a tiny outpost';

  const rows = shop.items.map(it => {
    const owned = ownedOf(captain, it.itemId);
    const canAfford = captain.reales >= it.buyPrice;
    return `
      <div class="item-row">
        <div>
          <div>
            <span class="name">${esc(it.name)}</span>
            <span class="price">${it.buyPrice} reales</span>
            <span class="unit">per ${esc(it.unit)}</span>
            <span class="stock"> · ${it.stockQuantity} in stock</span>
          </div>
          <div class="desc">${esc(it.description)}</div>
        </div>
        <div class="controls">
          <span class="owned">${owned > 0 ? `× ${owned}` : ''}</span>
          <input type="number" min="1" value="1" data-island-qty="${esc(it.itemId)}" />
          <button data-island-buy="${esc(it.itemId)}" ${canAfford ? '' : 'disabled'}>Buy</button>
          <button class="secondary" data-island-sell="${esc(it.itemId)}" ${owned > 0 ? '' : 'disabled'}>Sell (${it.sellPrice})</button>
        </div>
      </div>
    `;
  }).join('');

  // Cargo the shop doesn't stock — fish, whale meat, salvage, etc. — still sells
  // at the standard sell fraction. Lets the captain offload anything for reales.
  const stockedIds = new Set(shop.items.map(s => s.itemId));
  const sellFrac = ISLAND_SELL_FRACTION_BY_SIZE[shop.islandSize] ?? 0.5;
  const cargoEntries = captain.inventory
    .filter(e => e.quantity >= 1 && !stockedIds.has(e.itemId) && !NEVER_SOLD.has(e.itemId))
    .map(e => {
      const item = getItem(e.itemId);
      if (!item) return null;
      const sellPrice = Math.max(1, Math.round(item.price * sellFrac));
      return { entry: e, item, sellPrice };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);

  const cargoRows = cargoEntries.map(({ entry, item, sellPrice }) => `
    <div class="item-row">
      <div>
        <div>
          <span class="name">${esc(item.name)}</span>
          <span class="price">${sellPrice} reales each</span>
          <span class="unit">per ${esc(item.unit)}</span>
        </div>
        <div class="desc">${esc(item.description)}</div>
      </div>
      <div class="controls">
        <span class="owned">× ${Math.floor(entry.quantity)}</span>
        <input type="number" min="1" max="${Math.floor(entry.quantity)}" value="1" data-island-qty="${esc(entry.itemId)}" />
        <button class="secondary" data-island-sell="${esc(entry.itemId)}">Sell</button>
      </div>
    </div>
  `).join('');

  setHTML(container, `
    <div class="island-shop">
      <h1 class="title">${esc(shop.islandName)}</h1>
      <p class="subtitle">${esc(sizeLabel)}</p>

      <div class="outfit-toolbar">
        <div class="toolbar-label">Captain ${esc(captain.username)}</div>
        <div class="purse">Purse: ${captain.reales.toLocaleString()} reales</div>
      </div>

      <p class="outfit-warning">
        Prices reflect what this island will charge a passing ship. Sell back for less.
      </p>

      <div class="item-list">${rows}</div>

      ${cargoEntries.length > 0 ? `
        <h2 style="margin-top: 28px;">Sell from your hold <span class="lede">— fish, salvage, anything spare</span></h2>
        <div class="item-list">${cargoRows}</div>
      ` : ''}

      <div class="set-sail-bar">
        <div style="color: var(--ink-soft); font-style: italic;">
          When you have finished trading, weigh anchor.
        </div>
        <button class="primary" id="leave-island-btn">Weigh Anchor</button>
      </div>
    </div>
  `);

  container.querySelectorAll<HTMLButtonElement>('[data-island-buy]').forEach(btn => {
    btn.onclick = () => {
      const id = btn.dataset.islandBuy!;
      const qtyEl = container.querySelector<HTMLInputElement>(`[data-island-qty="${id}"]`);
      const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
      conn.send({ type: 'buy_at_island', itemId: id, quantity: qty });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-island-sell]').forEach(btn => {
    btn.onclick = () => {
      const id = btn.dataset.islandSell!;
      const qtyEl = container.querySelector<HTMLInputElement>(`[data-island-qty="${id}"]`);
      const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
      conn.send({ type: 'sell_at_island', itemId: id, quantity: qty });
    };
  });
  const leaveBtn = container.querySelector<HTMLButtonElement>('#leave-island-btn');
  if (leaveBtn) leaveBtn.onclick = () => conn.send({ type: 'leave_island' });
}
